import logger from '@/plugins/logger'

// Browser side of Web Push, free of Vue so the mixin and main.js share it.
// Nothing here logs the subscription endpoint: it is the one secret a browser
// has, whoever knows it can push to this device.

const SW_URL = '/sw.js'

export function pushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
}

// iOS only offers Web Push to a site added to the home screen and opened from
// there; in the browser tab PushManager is simply missing.
export function isIosWithoutHomeScreen() {
  var ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  var standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true
  return ios && !standalone
}

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return Promise.resolve(null)
  }
  return navigator.serviceWorker.register(SW_URL).catch(err => {
    logger.warn(`Service worker registration failed: ${err}`)
    return null
  })
}

// getRegistration() rather than .ready: .ready never settles when the
// registration failed, and the start-up sync must not hang on that.
export async function currentPushSubscription() {
  if (!pushSupported()) return null
  var registration = await navigator.serviceWorker.getRegistration(SW_URL)
  return registration ? registration.pushManager.getSubscription() : null
}

export async function ensurePushSubscription(publicKey) {
  var registration = await navigator.serviceWorker.getRegistration(SW_URL)
    || await navigator.serviceWorker.register(SW_URL)
  await navigator.serviceWorker.ready

  var key = base64UrlToBytes(publicKey)
  var subscription = await registration.pushManager.getSubscription()

  // A subscription made under an older VAPID key is useless after a key
  // rotation: the push service would reject every message we sign.
  if (subscription && !sameKey(subscription.options.applicationServerKey, key)) {
    await subscription.unsubscribe()
    subscription = null
  }

  return subscription || registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  })
}

export function subscriptionPayload(subscription) {
  var json = subscription.toJSON()
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  }
}

function base64UrlToBytes(value) {
  var padded = (value + '='.repeat((4 - value.length % 4) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  var raw = window.atob(padded)
  var bytes = new Uint8Array(raw.length)
  for (var i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i)
  }
  return bytes
}

// Some browsers (Firefox) report applicationServerKey as null even for a
// subscription made with one. Treat that as "unchanged": resubscribing would
// hand us a new endpoint on every activation and orphan the server-side
// subscriptions of all other trips this device follows.
function sameKey(buffer, bytes) {
  if (!buffer) return true
  var existing = new Uint8Array(buffer)
  return existing.length === bytes.length && existing.every((byte, i) => byte === bytes[i])
}
